import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'

export const supportedLanguages = ['en', 'hi', 'es', 'fr', 'de'] as const
export type PortalLanguage = (typeof supportedLanguages)[number]

export const languageLocales: Record<PortalLanguage, string> = {
  en: 'en-IN',
  hi: 'hi-IN',
  es: 'es-ES',
  fr: 'fr-FR',
  de: 'de-DE',
}

const en = {
  app: {
    name: 'capSpire Incident Knowledge Base',
    internal: 'Internal',
  },
  nav: {
    main: 'Main',
    dashboard: 'Dashboard',
    myIncidents: 'My Submissions',
    incidentsSupport: 'Incidents Support',
    knowledge: 'Search Knowledge Base',
    knowledgeBase: 'Knowledge Base',
    confluence: 'Confluence',
    incidentsBrowse: 'Incidents',
    sharepoint: 'SharePoint',
    learnings: 'Learnings Library',
    solutions: 'Article Catalogue',
    admin: 'Admin',
    system: 'System',
    analytics: 'Analytics',
    dataQuality: 'Data Quality',
    ingestionRuns: 'Ingestion Runs',
    fileDrop: 'Bulk Upload',
    review: 'Review',
    account: 'Account',
    notifications: 'Notifications',
  },
  actions: {
    submitArticle: 'Submit Article',
    logIncident: 'Log incident',
    search: 'Search',
    searching: 'Searching…',
    clear: 'Clear',
    refresh: 'Refresh',
    save: 'Save',
    cancel: 'Cancel',
    close: 'Close',
    previous: 'Previous',
    next: 'Next',
    viewAll: 'View all →',
    readMore: 'Read more',
    showLess: 'Show less',
    submitForReview: 'Submit for review',
    markResolved: 'Mark Resolved',
    retry: 'Retry',
    exportPdf: 'Export PDF',
  },
  pages: {
    dashboard: ['Dashboard', 'Overview of incidents, knowledge and activity'],
    mine: ['My Submissions', 'Incidents logged by you'],
    tickets: ['Incidents Support', 'Work on your open and in-progress incidents'],
    knowledge: ['Search Knowledge Base', 'Incidents and Documents'],
    confluence: ['Confluence', 'Browse indexed Confluence pages'],
    incidentsBrowse: ['Incidents', 'Browse all incidents'],
    sharepoint: ['SharePoint', 'Browse indexed SharePoint documents'],
    learnings: ['Learnings Library', 'Lessons captured from resolved incidents'],
    solutions: ['Article Catalogue', 'Verified root causes and resolution steps'],
    system: ['System', 'Application activity, service health and configuration'],
    analytics: ['Analytics', 'Incident and knowledge trends'],
    dataQuality: ['Data Quality', 'Check that knowledge and search remain synchronized'],
    ingestion: ['Ingestion Runs', 'Current pipeline status and run history'],
    upload: ['Bulk Upload', 'Upload and manage imported knowledge'],
    review: ['Review', 'Approve or reject submitted articles and incidents'],
    notifications: ['Notifications', 'Recent alerts and updates'],
  },
  search: {
    description: 'Search incidents and indexed knowledge using keyword, meaning or hybrid search.',
    placeholder: 'e.g. database timeout, pricing failure, HTTP 503, incident number…',
    exact: 'Exact',
    meaning: 'Meaning',
    hybrid: 'Hybrid',
    incidentsIndexed: 'incidents indexed',
    confluenceIndexed: 'Confluence docs indexed',
    sharepointIndexed: 'SharePoint docs indexed',
    sourcesActive: 'sources active',
    engine: 'Search engine',
    savedAnswers: 'Saved AI answers',
    latestAnswers: 'Showing latest 20 answers',
    recoverAnswer: 'Recover a previous generated answer…',
    storedIncidents: 'stored incidents',
    matchingIncidents: 'matching incidents',
    noMatches: 'No matching incidents',
    tryAnother: 'Try another search or clear the current filter.',
  },
  settings: {
    activityTab: 'Activity',
    searchTab: 'Resolution Tuning',
    aiModelsTab: 'AI Models',
    confluenceTab: 'Confluence',
    sharepointTab: 'SharePoint',
    ingestionTab: 'Ingestion',
    systemTab: 'System Status',
    testsTab: 'Test Center',
    notificationsTab: 'Notifications',
    authenticationTab: 'Authentication',
    searchPreferences: 'Search Preferences',
    languageAndAppearance: 'Language and appearance',
    language: 'Language',
    languageHelp: 'Choose the language used for portal labels, dates and AI responses.',
    theme: 'Theme',
    defaultSearchMode: 'Default search mode',
    defaultResults: 'Default results',
    saveSearch: 'Save search settings',
    saveAppearance: 'Save appearance settings',
    saved: 'Settings saved and applied to your profile.',
  },
  languages: {
    en: 'English',
    hi: 'हिन्दी',
    es: 'Español',
    fr: 'Français',
    de: 'Deutsch',
  },
  incident: {
    logTitle: 'Log incident',
    logHelp: 'Only log incidents that have already been resolved. Once reviewed and approved, it becomes part of the searchable Knowledge Base.',
    number: 'Incident number',
    date: 'Incident date',
    title: 'Title',
    application: 'Application',
    environment: 'Environment',
    severity: 'Severity',
    category: 'Category',
    owner: 'Owner',
    status: 'Status',
    description: 'Description',
    errorMessage: 'Error message',
    rootCause: 'Root cause',
    resolutionSteps: 'Resolution steps',
    tags: 'Tags',
    lessons: 'Lessons learned',
    markInternal: 'Mark as internal',
    markInternalHelp: 'Only people at your company can open this directly. It still contributes to search and AI answers for everyone.',
  },
  article: {
    title: 'Submit Article for Review',
    help: 'Submit a solution or learning for review before it appears in Article Catalogue.',
    articleTitle: 'Article title',
    category: 'Category',
    severity: 'Severity',
    description: 'Description / Resolution Steps',
    tags: 'Tags',
  },
  common: {
    all: 'All',
    critical: 'Critical',
    high: 'High',
    medium: 'Medium',
    low: 'Low',
    open: 'Open',
    inProgress: 'In Progress',
    resolved: 'Resolved',
    closed: 'Closed',
    loading: 'Loading…',
    configured: 'Configured',
    connected: 'Connected',
    healthy: 'Healthy',
    unavailable: 'Unavailable',
    noRecentActivity: 'No recent activity',
    pageOf: 'Page {{page}} of {{pages}}',
  },
}

const hi = {
  app: { name: 'capSpire Incident Knowledge Base', internal: 'आंतरिक' },
  nav: {
    main: 'मुख्य', dashboard: 'डैशबोर्ड', myIncidents: 'मेरे सबमिशन', incidentsSupport: 'इंसिडेंट सहायता',
    knowledge: 'ज्ञान आधार खोजें', knowledgeBase: 'ज्ञान आधार',
    confluence: 'Confluence', incidentsBrowse: 'इंसिडेंट', sharepoint: 'SharePoint',
    learnings: 'लर्निंग लाइब्रेरी', solutions: 'लेख सूची',
    admin: 'एडमिन', system: 'सिस्टम', analytics: 'विश्लेषण', dataQuality: 'डेटा गुणवत्ता',
    ingestionRuns: 'इंजेशन रन', fileDrop: 'बल्क अपलोड', review: 'समीक्षा', account: 'खाता', notifications: 'सूचनाएँ',
  },
  actions: {
    submitArticle: 'लेख सबमिट करें', logIncident: 'इंसिडेंट दर्ज करें', search: 'खोजें', searching: 'खोज जारी है…',
    clear: 'साफ़ करें', refresh: 'रीफ़्रेश', save: 'सहेजें', cancel: 'रद्द करें', close: 'बंद करें',
    previous: 'पिछला', next: 'अगला', viewAll: 'सभी देखें →', readMore: 'और पढ़ें', showLess: 'कम दिखाएँ',
    submitForReview: 'समीक्षा के लिए भेजें', markResolved: 'समाधान हुआ', retry: 'फिर प्रयास करें', exportPdf: 'PDF निर्यात करें',
  },
  pages: {
    dashboard: ['डैशबोर्ड', 'इंसिडेंट, ज्ञान और गतिविधि का सार'],
    mine: ['मेरे सबमिशन', 'आपके द्वारा दर्ज इंसिडेंट'],
    tickets: ['इंसिडेंट सहायता', 'अपने खुले और प्रगति वाले इंसिडेंट पर काम करें'],
    knowledge: ['ज्ञान आधार खोजें', 'इंसिडेंट और दस्तावेज़'],
    confluence: ['Confluence', 'इंडेक्स किए गए Confluence पेज ब्राउज़ करें'],
    incidentsBrowse: ['इंसिडेंट', 'सभी इंसिडेंट ब्राउज़ करें'],
    sharepoint: ['SharePoint', 'इंडेक्स किए गए SharePoint दस्तावेज़ ब्राउज़ करें'],
    learnings: ['लर्निंग लाइब्रेरी', 'समाधान किए गए इंसिडेंट से सीख'],
    solutions: ['लेख सूची', 'सत्यापित मूल कारण और समाधान चरण'],
    system: ['सिस्टम', 'एप्लिकेशन गतिविधि, सेवा स्वास्थ्य और कॉन्फ़िगरेशन'],
    analytics: ['विश्लेषण', 'इंसिडेंट और ज्ञान रुझान'],
    dataQuality: ['डेटा गुणवत्ता', 'ज्ञान और खोज का तालमेल जाँचें'],
    ingestion: ['इंजेशन रन', 'वर्तमान पाइपलाइन स्थिति और इतिहास'],
    upload: ['बल्क अपलोड', 'आयातित ज्ञान अपलोड और प्रबंधित करें'],
    review: ['समीक्षा', 'सबमिट किए गए लेख और इंसिडेंट को स्वीकृत या अस्वीकृत करें'],
    notifications: ['सूचनाएँ', 'हाल की चेतावनियाँ और अपडेट'],
  },
  search: {
    description: 'कीवर्ड, अर्थ या हाइब्रिड खोज से इंसिडेंट और ज्ञान खोजें।',
    placeholder: 'जैसे डेटाबेस टाइमआउट, प्राइसिंग त्रुटि, HTTP 503…', exact: 'सटीक', meaning: 'अर्थ',
    hybrid: 'हाइब्रिड', incidentsIndexed: 'इंसिडेंट इंडेक्स किए गए', confluenceIndexed: 'Confluence दस्तावेज़ इंडेक्स किए गए',
    sharepointIndexed: 'SharePoint दस्तावेज़ इंडेक्स किए गए', sourcesActive: 'स्रोत सक्रिय', engine: 'खोज इंजन',
    savedAnswers: 'सहेजे गए AI उत्तर', latestAnswers: 'नवीनतम 20 उत्तर', recoverAnswer: 'पिछला AI उत्तर खोलें…',
    storedIncidents: 'संग्रहित इंसिडेंट', matchingIncidents: 'मिलते इंसिडेंट', noMatches: 'कोई इंसिडेंट नहीं मिला',
    tryAnother: 'दूसरी खोज करें या वर्तमान फ़िल्टर हटाएँ।',
  },
  settings: {
    activityTab: 'गतिविधि',
    searchTab: 'समाधान ट्यूनिंग', aiModelsTab: 'AI मॉडल', confluenceTab: 'Confluence', sharepointTab: 'SharePoint',
    ingestionTab: 'इंजेशन', systemTab: 'सिस्टम स्थिति', testsTab: 'टेस्ट सेंटर', notificationsTab: 'सूचनाएँ',
    authenticationTab: 'प्रमाणीकरण', searchPreferences: 'खोज पसंद', languageAndAppearance: 'भाषा और रूप',
    language: 'भाषा', languageHelp: 'पोर्टल लेबल, तारीख और AI उत्तर की भाषा चुनें।', theme: 'थीम',
    defaultSearchMode: 'डिफ़ॉल्ट खोज प्रकार', defaultResults: 'डिफ़ॉल्ट परिणाम',
    saveSearch: 'खोज सेटिंग्स सहेजें', saveAppearance: 'रूप सेटिंग्स सहेजें', saved: 'सेटिंग्स आपकी प्रोफ़ाइल पर लागू हो गई हैं।',
  },
  languages: { en: 'English', hi: 'हिन्दी', es: 'Español', fr: 'Français', de: 'Deutsch' },
  incident: {
    logTitle: 'इंसिडेंट दर्ज करें', logHelp: 'केवल वही इंसिडेंट दर्ज करें जो पहले से हल हो चुकी हो। समीक्षा और स्वीकृति के बाद यह सर्च करने योग्य नॉलेज बेस का हिस्सा बन जाएगी।',
    number: 'इंसिडेंट नंबर', date: 'इंसिडेंट तारीख', title: 'शीर्षक', application: 'एप्लिकेशन',
    environment: 'परिवेश', severity: 'गंभीरता', category: 'श्रेणी', owner: 'मालिक', status: 'स्थिति',
    description: 'विवरण', errorMessage: 'त्रुटि संदेश', rootCause: 'मूल कारण', resolutionSteps: 'समाधान चरण',
    tags: 'टैग', lessons: 'सीख',
    markInternal: 'आंतरिक के रूप में चिह्नित करें',
    markInternalHelp: 'केवल आपकी कंपनी के लोग ही इसे सीधे खोल सकते हैं। यह सभी के लिए खोज और AI उत्तरों में योगदान देता रहेगा।',
  },
  article: {
    title: 'लेख समीक्षा के लिए भेजें', help: 'लेख सूची में आने से पहले लेख समीक्षा के लिए भेजें।',
    articleTitle: 'लेख शीर्षक', category: 'श्रेणी', severity: 'गंभीरता',
    description: 'विवरण / समाधान चरण', tags: 'टैग',
  },
  common: {
    all: 'सभी', critical: 'गंभीर', high: 'उच्च', medium: 'मध्यम', low: 'कम', open: 'खुला',
    inProgress: 'प्रगति में', resolved: 'समाधान हुआ', closed: 'बंद', loading: 'लोड हो रहा है…',
    configured: 'कॉन्फ़िगर है', connected: 'जुड़ा है', healthy: 'स्वस्थ', unavailable: 'अनुपलब्ध',
    noRecentActivity: 'हाल की गतिविधि नहीं', pageOf: 'पृष्ठ {{page}} / {{pages}}',
  },
}

const es = {
  app: { name: 'capSpire Incident Knowledge Base', internal: 'Interno' },
  nav: {
    main: 'Principal', dashboard: 'Panel', myIncidents: 'Mis envíos', incidentsSupport: 'Soporte de incidentes',
    knowledge: 'Buscar base de conocimiento', knowledgeBase: 'Base de conocimiento',
    confluence: 'Confluence', incidentsBrowse: 'Incidentes', sharepoint: 'SharePoint',
    learnings: 'Biblioteca de aprendizaje',
    solutions: 'Catálogo de artículos', admin: 'Administración', system: 'Sistema', analytics: 'Analítica',
    dataQuality: 'Calidad de datos', ingestionRuns: 'Ejecuciones de ingesta', fileDrop: 'Carga masiva',
    review: 'Revisión', account: 'Cuenta', notifications: 'Notificaciones',
  },
  actions: {
    submitArticle: 'Enviar artículo', logIncident: 'Registrar incidente', search: 'Buscar', searching: 'Buscando…',
    clear: 'Limpiar', refresh: 'Actualizar', save: 'Guardar', cancel: 'Cancelar', close: 'Cerrar',
    previous: 'Anterior', next: 'Siguiente', viewAll: 'Ver todo →', readMore: 'Leer más', showLess: 'Ver menos',
    submitForReview: 'Enviar para revisión', markResolved: 'Marcar resuelto', retry: 'Reintentar', exportPdf: 'Exportar PDF',
  },
  pages: {
    dashboard: ['Panel', 'Resumen de incidentes, conocimiento y actividad'],
    mine: ['Mis envíos', 'Incidentes registrados por usted'],
    tickets: ['Soporte de incidentes', 'Trabaje en sus incidentes abiertos o en curso'],
    knowledge: ['Buscar base de conocimiento', 'Incidentes y documentos'],
    confluence: ['Confluence', 'Explore las páginas de Confluence indexadas'],
    incidentsBrowse: ['Incidentes', 'Explore todos los incidentes'],
    sharepoint: ['SharePoint', 'Explore los documentos de SharePoint indexados'],
    learnings: ['Biblioteca de aprendizaje', 'Lecciones de incidentes resueltos'],
    solutions: ['Catálogo de artículos', 'Causas y pasos de solución verificados'],
    system: ['Sistema', 'Actividad, estado de servicios y configuración'],
    analytics: ['Analítica', 'Tendencias de incidentes y conocimiento'],
    dataQuality: ['Calidad de datos', 'Compruebe la sincronización del conocimiento y la búsqueda'],
    ingestion: ['Ejecuciones de ingesta', 'Estado actual e historial de canalizaciones'],
    upload: ['Carga masiva', 'Cargue y gestione conocimiento importado'],
    review: ['Revisión', 'Apruebe o rechace artículos e incidentes enviados'],
    notifications: ['Notificaciones', 'Alertas y actualizaciones recientes'],
  },
  search: {
    description: 'Busque incidentes y conocimiento por palabras, significado o modo híbrido.',
    placeholder: 'p. ej., tiempo de espera, error de precios, HTTP 503…', exact: 'Exacta', meaning: 'Semántica',
    hybrid: 'Híbrida', incidentsIndexed: 'incidentes indexados', confluenceIndexed: 'documentos de Confluence indexados',
    sharepointIndexed: 'documentos de SharePoint indexados', sourcesActive: 'fuentes activas', engine: 'Motor de búsqueda',
    savedAnswers: 'Respuestas de AI guardadas', latestAnswers: 'Últimas 20 respuestas', recoverAnswer: 'Recuperar una respuesta anterior…',
    storedIncidents: 'incidentes almacenados', matchingIncidents: 'incidentes encontrados',
    noMatches: 'No hay incidentes coincidentes', tryAnother: 'Pruebe otra búsqueda o quite el filtro actual.',
  },
  settings: {
    activityTab: 'Actividad',
    searchTab: 'Ajuste de resolución', aiModelsTab: 'Modelos de AI', confluenceTab: 'Confluence', sharepointTab: 'SharePoint',
    ingestionTab: 'Ingesta', systemTab: 'Estado del sistema', testsTab: 'Centro de pruebas',
    notificationsTab: 'Notificaciones', authenticationTab: 'Autenticación', searchPreferences: 'Preferencias de búsqueda',
    languageAndAppearance: 'Idioma y apariencia', language: 'Idioma',
    languageHelp: 'Elija el idioma de las etiquetas, fechas y respuestas de AI.', theme: 'Tema',
    defaultSearchMode: 'Modo de búsqueda predeterminado', defaultResults: 'Resultados predeterminados',
    saveSearch: 'Guardar configuración de búsqueda', saveAppearance: 'Guardar configuración de apariencia', saved: 'La configuración se aplicó a su perfil.',
  },
  languages: { en: 'English', hi: 'हिन्दी', es: 'Español', fr: 'Français', de: 'Deutsch' },
  incident: {
    logTitle: 'Registrar incidente', logHelp: 'Registre únicamente incidentes que ya se hayan resuelto. Una vez revisado y aprobado, pasará a formar parte de la Base de Conocimiento consultable.',
    number: 'Número de incidente', date: 'Fecha', title: 'Título', application: 'Aplicación', environment: 'Entorno',
    severity: 'Severidad', category: 'Categoría', owner: 'Responsable', status: 'Estado', description: 'Descripción',
    errorMessage: 'Mensaje de error', rootCause: 'Causa raíz', resolutionSteps: 'Pasos de solución',
    tags: 'Etiquetas', lessons: 'Lecciones aprendidas',
    markInternal: 'Marcar como interno',
    markInternalHelp: 'Solo las personas de su empresa pueden abrir esto directamente. Seguirá contribuyendo a la búsqueda y a las respuestas de IA para todos.',
  },
  article: {
    title: 'Enviar artículo para revisión', help: 'Envíe una solución para revisión antes de publicarla.',
    articleTitle: 'Título del artículo', category: 'Categoría', severity: 'Severidad',
    description: 'Descripción / Pasos de solución', tags: 'Etiquetas',
  },
  common: {
    all: 'Todos', critical: 'Crítica', high: 'Alta', medium: 'Media', low: 'Baja', open: 'Abierto',
    inProgress: 'En curso', resolved: 'Resuelto', closed: 'Cerrado', loading: 'Cargando…', configured: 'Configurado',
    connected: 'Conectado', healthy: 'Correcto', unavailable: 'No disponible',
    noRecentActivity: 'Sin actividad reciente', pageOf: 'Página {{page}} de {{pages}}',
  },
}

const fr = {
  app: { name: 'capSpire Incident Knowledge Base', internal: 'Interne' },
  nav: {
    main: 'Principal', dashboard: 'Tableau de bord', myIncidents: 'Mes soumissions',
    incidentsSupport: 'Assistance incidents', knowledge: 'Rechercher la base de connaissances', knowledgeBase: 'Base de connaissances',
    confluence: 'Confluence', incidentsBrowse: 'Incidents', sharepoint: 'SharePoint',
    learnings: 'Bibliothèque des apprentissages', solutions: 'Catalogue d’articles', admin: 'Administration',
    system: 'Système', analytics: 'Analyses', dataQuality: 'Qualité des données',
    ingestionRuns: 'Exécutions d’ingestion', fileDrop: 'Téléversement en masse', review: 'Révision', account: 'Compte',
    notifications: 'Notifications',
  },
  actions: {
    submitArticle: 'Soumettre un article', logIncident: 'Enregistrer un incident', search: 'Rechercher',
    searching: 'Recherche…', clear: 'Effacer', refresh: 'Actualiser', save: 'Enregistrer', cancel: 'Annuler',
    close: 'Fermer', previous: 'Précédent', next: 'Suivant', viewAll: 'Tout voir →', readMore: 'Lire plus',
    showLess: 'Voir moins', submitForReview: 'Soumettre pour révision', markResolved: 'Marquer résolu', retry: 'Réessayer', exportPdf: 'Exporter en PDF',
  },
  pages: {
    dashboard: ['Tableau de bord', 'Vue d’ensemble des incidents, connaissances et activités'],
    mine: ['Mes soumissions', 'Incidents enregistrés par vous'],
    tickets: ['Assistance incidents', 'Traitez vos incidents ouverts ou en cours'],
    knowledge: ['Rechercher la base de connaissances', 'Incidents et documents'],
    confluence: ['Confluence', 'Parcourir les pages Confluence indexées'],
    incidentsBrowse: ['Incidents', 'Parcourir tous les incidents'],
    sharepoint: ['SharePoint', 'Parcourir les documents SharePoint indexés'],
    learnings: ['Bibliothèque des apprentissages', 'Leçons tirées des incidents résolus'],
    solutions: ['Catalogue d’articles', 'Causes et étapes de résolution vérifiées'],
    system: ['Système', 'Activité, état des services et configuration'],
    analytics: ['Analyses', 'Tendances des incidents et des connaissances'],
    dataQuality: ['Qualité des données', 'Vérifiez la synchronisation des connaissances et de la recherche'],
    ingestion: ['Exécutions d’ingestion', 'État actuel et historique des pipelines'],
    upload: ['Téléversement en masse', 'Importez et gérez les connaissances'],
    review: ['Révision', 'Approuvez ou rejetez les articles et incidents soumis'],
    notifications: ['Notifications', 'Alertes et mises à jour récentes'],
  },
  search: {
    description: 'Recherchez par mot-clé, sens ou mode hybride.',
    placeholder: 'ex. délai de base de données, erreur de prix, HTTP 503…', exact: 'Exacte', meaning: 'Sémantique',
    hybrid: 'Hybride', incidentsIndexed: 'incidents indexés', confluenceIndexed: 'documents Confluence indexés',
    sharepointIndexed: 'documents SharePoint indexés', sourcesActive: 'sources actives', engine: 'Moteur de recherche',
    savedAnswers: 'Réponses AI enregistrées', latestAnswers: '20 dernières réponses',
    recoverAnswer: 'Récupérer une réponse précédente…', storedIncidents: 'incidents enregistrés',
    matchingIncidents: 'incidents correspondants', noMatches: 'Aucun incident correspondant',
    tryAnother: 'Essayez une autre recherche ou effacez le filtre.',
  },
  settings: {
    activityTab: 'Activité',
    searchTab: 'Réglage de résolution', aiModelsTab: 'Modèles AI', confluenceTab: 'Confluence', sharepointTab: 'SharePoint',
    ingestionTab: 'Ingestion', systemTab: 'État du système', testsTab: 'Centre de test',
    notificationsTab: 'Notifications', authenticationTab: 'Authentification',
    searchPreferences: 'Préférences de recherche', languageAndAppearance: 'Langue et apparence', language: 'Langue',
    languageHelp: 'Choisissez la langue des libellés, dates et réponses AI.', theme: 'Thème',
    defaultSearchMode: 'Mode de recherche par défaut', defaultResults: 'Résultats par défaut',
    saveSearch: 'Enregistrer les paramètres de recherche', saveAppearance: 'Enregistrer les paramètres d’apparence', saved: 'Les paramètres ont été appliqués à votre profil.',
  },
  languages: { en: 'English', hi: 'हिन्दी', es: 'Español', fr: 'Français', de: 'Deutsch' },
  incident: {
    logTitle: 'Enregistrer un incident', logHelp: 'N’enregistrez que des incidents déjà résolus. Une fois vérifié et approuvé, il fera partie de la Base de connaissances consultable.',
    number: 'Numéro d’incident', date: 'Date', title: 'Titre', application: 'Application', environment: 'Environnement',
    severity: 'Sévérité', category: 'Catégorie', owner: 'Responsable', status: 'Statut', description: 'Description',
    errorMessage: 'Message d’erreur', rootCause: 'Cause racine', resolutionSteps: 'Étapes de résolution',
    tags: 'Étiquettes', lessons: 'Leçons apprises',
    markInternal: 'Marquer comme interne',
    markInternalHelp: 'Seules les personnes de votre entreprise peuvent l’ouvrir directement. Cela continue de contribuer à la recherche et aux réponses de l’IA pour tous.',
  },
  article: {
    title: 'Soumettre l’article pour révision', help: 'Soumettez une solution avant sa publication.',
    articleTitle: 'Titre de l’article', category: 'Catégorie', severity: 'Sévérité',
    description: 'Description / Étapes de résolution', tags: 'Étiquettes',
  },
  common: {
    all: 'Tous', critical: 'Critique', high: 'Élevée', medium: 'Moyenne', low: 'Faible', open: 'Ouvert',
    inProgress: 'En cours', resolved: 'Résolu', closed: 'Fermé', loading: 'Chargement…', configured: 'Configuré',
    connected: 'Connecté', healthy: 'Opérationnel', unavailable: 'Indisponible',
    noRecentActivity: 'Aucune activité récente', pageOf: 'Page {{page}} sur {{pages}}',
  },
}

const de = {
  app: { name: 'capSpire Incident Knowledge Base', internal: 'Intern' },
  nav: {
    main: 'Hauptbereich', dashboard: 'Übersicht', myIncidents: 'Meine Einreichungen',
    incidentsSupport: 'Störungshilfe', knowledge: 'Wissensdatenbank durchsuchen', knowledgeBase: 'Wissensdatenbank',
    confluence: 'Confluence', incidentsBrowse: 'Störungen', sharepoint: 'SharePoint',
    learnings: 'Lernbibliothek', solutions: 'Artikelkatalog', admin: 'Administration', system: 'System',
    analytics: 'Analysen', dataQuality: 'Datenqualität', ingestionRuns: 'Importläufe', fileDrop: 'Massen-Upload',
    review: 'Überprüfung', account: 'Konto', notifications: 'Benachrichtigungen',
  },
  actions: {
    submitArticle: 'Artikel einreichen', logIncident: 'Störung erfassen', search: 'Suchen', searching: 'Suche…',
    clear: 'Löschen', refresh: 'Aktualisieren', save: 'Speichern', cancel: 'Abbrechen', close: 'Schließen',
    previous: 'Zurück', next: 'Weiter', viewAll: 'Alle anzeigen →', readMore: 'Mehr lesen', showLess: 'Weniger anzeigen',
    submitForReview: 'Zur Prüfung einreichen', markResolved: 'Als gelöst markieren', retry: 'Erneut versuchen', exportPdf: 'PDF exportieren',
  },
  pages: {
    dashboard: ['Übersicht', 'Überblick über Störungen, Wissen und Aktivitäten'],
    mine: ['Meine Einreichungen', 'Von Ihnen erfasste Störungen'],
    tickets: ['Störungshilfe', 'Bearbeiten Sie offene und laufende Störungen'],
    knowledge: ['Wissensdatenbank durchsuchen', 'Störungen und Dokumente'],
    confluence: ['Confluence', 'Indizierte Confluence-Seiten durchsuchen'],
    incidentsBrowse: ['Störungen', 'Alle Störungen durchsuchen'],
    sharepoint: ['SharePoint', 'Indizierte SharePoint-Dokumente durchsuchen'],
    learnings: ['Lernbibliothek', 'Erkenntnisse aus gelösten Störungen'],
    solutions: ['Artikelkatalog', 'Geprüfte Ursachen und Lösungsschritte'],
    system: ['System', 'Aktivität, Dienststatus und Konfiguration'],
    analytics: ['Analysen', 'Trends zu Störungen und Wissen'],
    dataQuality: ['Datenqualität', 'Synchronisierung von Wissen und Suche prüfen'],
    ingestion: ['Importläufe', 'Aktueller Pipeline-Status und Verlauf'],
    upload: ['Massen-Upload', 'Importiertes Wissen hochladen und verwalten'],
    review: ['Überprüfung', 'Eingereichte Artikel und Störungen genehmigen oder ablehnen'],
    notifications: ['Benachrichtigungen', 'Aktuelle Warnungen und Neuigkeiten'],
  },
  search: {
    description: 'Störungen und Wissen per Stichwort, Bedeutung oder hybrid suchen.',
    placeholder: 'z. B. Datenbank-Zeitüberschreitung, Preisfehler, HTTP 503…', exact: 'Exakt', meaning: 'Semantisch',
    hybrid: 'Hybrid', incidentsIndexed: 'Störungen indexiert', confluenceIndexed: 'Confluence-Dokumente indexiert',
    sharepointIndexed: 'SharePoint-Dokumente indexiert', sourcesActive: 'Quellen aktiv', engine: 'Suchmaschine',
    savedAnswers: 'Gespeicherte AI-Antworten', latestAnswers: 'Neueste 20 Antworten',
    recoverAnswer: 'Frühere Antwort wiederherstellen…', storedIncidents: 'gespeicherte Störungen',
    matchingIncidents: 'passende Störungen', noMatches: 'Keine passenden Störungen',
    tryAnother: 'Versuchen Sie eine andere Suche oder löschen Sie den Filter.',
  },
  settings: {
    activityTab: 'Aktivität',
    searchTab: 'Lösungsoptimierung', aiModelsTab: 'AI-Modelle', confluenceTab: 'Confluence', sharepointTab: 'SharePoint',
    ingestionTab: 'Ingestion', systemTab: 'Systemstatus', testsTab: 'Testcenter',
    notificationsTab: 'Benachrichtigungen', authenticationTab: 'Authentifizierung',
    searchPreferences: 'Sucheinstellungen', languageAndAppearance: 'Sprache und Darstellung', language: 'Sprache',
    languageHelp: 'Wählen Sie die Sprache für Beschriftungen, Datumswerte und AI-Antworten.', theme: 'Design',
    defaultSearchMode: 'Standardsuche', defaultResults: 'Standardergebnisse',
    saveSearch: 'Sucheinstellungen speichern', saveAppearance: 'Darstellungseinstellungen speichern', saved: 'Die Einstellungen wurden auf Ihr Profil angewendet.',
  },
  languages: { en: 'English', hi: 'हिन्दी', es: 'Español', fr: 'Français', de: 'Deutsch' },
  incident: {
    logTitle: 'Störung erfassen', logHelp: 'Erfassen Sie nur bereits gelöste Störungen. Nach Prüfung und Freigabe wird sie Teil der durchsuchbaren Wissensdatenbank.',
    number: 'Störungsnummer', date: 'Datum', title: 'Titel', application: 'Anwendung', environment: 'Umgebung',
    severity: 'Schweregrad', category: 'Kategorie', owner: 'Verantwortlich', status: 'Status', description: 'Beschreibung',
    errorMessage: 'Fehlermeldung', rootCause: 'Ursache', resolutionSteps: 'Lösungsschritte',
    tags: 'Tags', lessons: 'Erkenntnisse',
    markInternal: 'Als intern markieren',
    markInternalHelp: 'Nur Personen in Ihrem Unternehmen können dies direkt öffnen. Es trägt weiterhin zur Suche und zu KI-Antworten für alle bei.',
  },
  article: {
    title: 'Artikel zur Prüfung einreichen', help: 'Reichen Sie eine Lösung vor der Veröffentlichung zur Prüfung ein.',
    articleTitle: 'Artikeltitel', category: 'Kategorie', severity: 'Schweregrad',
    description: 'Beschreibung / Lösungsschritte', tags: 'Tags',
  },
  common: {
    all: 'Alle', critical: 'Kritisch', high: 'Hoch', medium: 'Mittel', low: 'Niedrig', open: 'Offen',
    inProgress: 'In Bearbeitung', resolved: 'Gelöst', closed: 'Geschlossen', loading: 'Wird geladen…',
    configured: 'Konfiguriert', connected: 'Verbunden', healthy: 'Verfügbar', unavailable: 'Nicht verfügbar',
    noRecentActivity: 'Keine aktuelle Aktivität', pageOf: 'Seite {{page}} von {{pages}}',
  },
}

void i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    hi: { translation: hi },
    es: { translation: es },
    fr: { translation: fr },
    de: { translation: de },
  },
  lng: 'en',
  fallbackLng: 'en',
  interpolation: { escapeValue: false },
  returnObjects: true,
})

export default i18n
